import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, X, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { GRADE_OPTIONS } from "@/lib/athleteTaxonomy";
import SportsPicker from "@/components/SportsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyAthlete = () => ({ first_name: "", last_name: "", grade: "", sports: [] });

export default function Signup() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const inviteToken = params.get("invite") || null;

    const [step, setStep] = useState("form");
    const [busy, setBusy] = useState(false);
    const [inviteChecked, setInviteChecked] = useState(!inviteToken);
    const [inviteValid, setInviteValid] = useState(false);

    const [name, setName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [note, setNote] = useState("");
    const [athletes, setAthletes] = useState([emptyAthlete()]);
    const [website, setWebsite] = useState("");

    // Validate invite token + prefill form
    useEffect(() => {
        if (!inviteToken) return;
        axios.get(`${API}/signup/invite/${inviteToken}`)
            .then((r) => {
                setInviteValid(true);
                if (r.data?.email) setEmail(r.data.email);
                if (r.data?.name) {
                    setName(r.data.name);
                    const parts = r.data.name.trim().split(/\s+/);
                    if (parts.length >= 2) {
                        setFirstName(parts[0]);
                        setLastName(parts.slice(1).join(" "));
                    } else if (parts.length === 1) {
                        setFirstName(parts[0]);
                    }
                }
            })
            .catch(() => {
                setInviteValid(false);
                toast.error("This invite link is invalid or has already been used. Ask your coach for a fresh link.");
            })
            .finally(() => setInviteChecked(true));
    }, [inviteToken]);

    const updateAthlete = (i, patch) => {
        setAthletes((arr) => arr.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
    };
    const addAthlete = () => setAthletes((arr) => [...arr, emptyAthlete()]);
    const removeAthlete = (i) => setAthletes((arr) => arr.filter((_, idx) => idx !== i));

    const submit = async () => {
        const first = firstName.trim();
        const last = lastName.trim();
        if (!first || !last) {
            toast.error("Please enter your first AND last name (real names, not a username).");
            return;
        }
        if (!email.trim()) {
            toast.error("Email is required");
            return;
        }
        const composedName = `${first} ${last}`;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/signup`, {
                name: composedName,
                first_name: first,
                last_name: last,
                email: email.trim(),
                phone: phone.trim() || null,
                how_we_know_you: note.trim() || null,
                athletes: athletes
                    .filter((a) => a.first_name.trim())
                    .map((a) => ({
                        first_name: a.first_name.trim(),
                        last_name: a.last_name.trim() || null,
                        grade: a.grade || null,
                        sports: a.sports,
                    })),
                invite_token: inviteToken,
                website,  // honeypot
            });
            setName(composedName);
            setStep(r.data?.via_invite ? "done_invite" : "done");
        } catch (e) {
            toast.error("Couldn't submit signup", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    if (step === "done" || step === "done_invite") {
        const wasInvite = step === "done_invite";
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="signup-success">
                <div className="max-w-lg text-center">
                    <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
                    <h1 className="text-3xl font-bold mb-3">Thanks, {name.split(" ")[0]}!</h1>
                    {wasInvite ? (
                        <p className="text-zinc-300 mb-2">Your account is ready. Check <span className="text-white">{email}</span> for a sign-in link — it'll take you straight into the profile wizard.</p>
                    ) : (
                        <p className="text-zinc-300 mb-2">Your signup is in. We review every request personally — you'll get an email from us shortly with a link to finish setting up your account.</p>
                    )}
                    <p className="text-zinc-500 text-sm mb-8">(Check spam, just in case.)</p>
                    <button type="button" onClick={() => navigate("/")} className="btn-tactical" data-testid="signup-back-home">
                        Back to home
                    </button>
                </div>
            </div>
        );
    }

    if (!inviteChecked) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="signup-checking-invite">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white" data-testid="signup-page">
            <div className="max-w-2xl mx-auto px-6 py-16">
                {inviteToken && inviteValid && (
                    <div className="bg-emerald-950/40 border border-emerald-800 rounded-lg p-4 mb-6 flex gap-3" data-testid="signup-invite-banner">
                        <Sparkles className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-semibold text-emerald-300">You've been personally invited</p>
                            <p className="text-emerald-200/80">Your coach already vetted you — finish this form and you'll get a sign-in link instantly.</p>
                        </div>
                    </div>
                )}

                <h1 className="text-4xl sm:text-5xl font-bold mb-3">Create your account</h1>
                <p className="text-zinc-400 mb-10">
                    {inviteToken && inviteValid
                        ? "Just confirm the details below and add your athletes."
                        : "Already train with us, or interested in joining? Fill this out and we'll get you set up."}
                </p>

                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="First name *" hint="Use your real first name — not a username or screen name.">
                            <input
                                data-testid="signup-first-name"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Jane"
                                className="w-full bg-zinc-950 border border-zinc-800 rounded px-4 py-3"
                                autoComplete="given-name"
                                autoFocus
                            />
                        </Field>
                        <Field label="Last name *">
                            <input
                                data-testid="signup-last-name"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Doe"
                                className="w-full bg-zinc-950 border border-zinc-800 rounded px-4 py-3"
                                autoComplete="family-name"
                            />
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Email *">
                            <input
                                data-testid="signup-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full bg-zinc-950 border border-zinc-800 rounded px-4 py-3"
                                autoComplete="email"
                            />
                        </Field>
                        <Field label="Phone (optional)">
                            <input
                                data-testid="signup-phone"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="(555) 555-5555"
                                className="w-full bg-zinc-950 border border-zinc-800 rounded px-4 py-3"
                                autoComplete="tel"
                            />
                        </Field>
                    </div>

                    <Field label="How do we know you? (optional)" hint="A quick note helps us spot fakes — e.g. 'My kids train Tuesdays/Thursdays with Sachin.'">
                        <textarea
                            data-testid="signup-note"
                            rows="3"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="A few words about why you're signing up…"
                            className="w-full bg-zinc-950 border border-zinc-800 rounded px-4 py-3"
                        />
                    </Field>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold">Athletes (optional)</label>
                            <button
                                type="button"
                                onClick={addAthlete}
                                className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                                data-testid="signup-add-athlete"
                            >
                                <Plus className="w-4 h-4" /> Add athlete
                            </button>
                        </div>
                        <p className="text-xs text-zinc-500 mb-3">Add your kids or yourself if you'll be the athlete.</p>

                        <div className="space-y-3">
                            {athletes.map((a, i) => (
                                <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`signup-athlete-${i}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs uppercase tracking-wide text-zinc-500">Athlete {i + 1}</span>
                                        {athletes.length > 1 && (
                                            <button type="button" onClick={() => removeAthlete(i)} className="text-zinc-500 hover:text-red-400" data-testid={`signup-athlete-remove-${i}`}>
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                                        <input
                                            type="text"
                                            value={a.first_name}
                                            onChange={(e) => updateAthlete(i, { first_name: e.target.value })}
                                            placeholder="First name"
                                            className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
                                            data-testid={`signup-athlete-first-${i}`}
                                        />
                                        <input
                                            type="text"
                                            value={a.last_name}
                                            onChange={(e) => updateAthlete(i, { last_name: e.target.value })}
                                            placeholder="Last name"
                                            className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
                                            data-testid={`signup-athlete-last-${i}`}
                                        />
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <select
                                            value={a.grade}
                                            onChange={(e) => updateAthlete(i, { grade: e.target.value })}
                                            className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
                                            data-testid={`signup-athlete-grade-${i}`}
                                        >
                                            <option value="">— Grade —</option>
                                            {GRADE_OPTIONS.map((g) => (
                                                <option key={g.value} value={g.value}>{g.label}</option>
                                            ))}
                                        </select>
                                        <div />
                                    </div>
                                    <div className="mt-3">
                                        <p className="text-xs text-zinc-500 mb-1">Sports (optional)</p>
                                        <SportsPicker
                                            value={a.sports}
                                            onChange={(sports) => updateAthlete(i, { sports })}
                                            testidPrefix={`signup-athlete-sports-${i}`}
                                            allowOther={false}
                                            placeholder="Type to add sports…"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Honeypot: real users can't see this */}
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                        aria-hidden="true"
                    />

                    <div className="flex justify-end pt-2">
                        <button
                            type="button"
                            onClick={submit}
                            disabled={busy || !name.trim() || !email.trim()}
                            className="btn-tactical disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="signup-submit"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit signup <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-sm font-semibold mb-1.5">{label}</label>
            {hint && <p className="text-xs text-zinc-500 mb-1.5">{hint}</p>}
            {children}
        </div>
    );
}
